import React from "react";
import { Link } from "react-router-dom";
import { Container, SectionLabel } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, Eye, FileSearch, Flag, Scale, Receipt } from "lucide-react";

const SAFETY_IMG = "https://images.pexels.com/photos/8466227/pexels-photo-8466227.jpeg";

const PILLARS = [
  { icon: FileSearch, t: "Campaign review", d: "Every campaign is reviewed before it can accept public donations. We check identity, beneficiary, and intended use." },
  { icon: Scale, t: "Risk-based verification", d: "Verification evidence scales with category and risk — higher goals and sensitive categories require more." },
  { icon: Lock, t: "Controlled payouts", d: "FightFund does not move money outside payment rails. Payouts release through Stripe after review readiness." },
  { icon: Eye, t: "Ongoing monitoring", d: "We monitor for duplicate images, donation velocity spikes, complaints, and beneficiary mismatches." },
  { icon: Flag, t: "Report & respond", d: "Anyone can report a campaign. Reports enter a Trust & Safety workflow and may create risk flags." },
  { icon: ShieldCheck, t: "Honest labels", d: "We show factual trust labels and never claim a campaign is 'guaranteed safe' or 'fraud-proof.'" },
  { icon: Receipt, t: "Transparent fees", d: "A flat 5% platform fee (3% for Pro) plus Stripe processing — always shown up front. No hidden fees or surprise charges, ever." },
];

export default function TrustSafety() {
  return (
    <div>
      <section className="relative overflow-hidden border-b border-ff-border bg-white">
        <Container className="grid gap-12 py-16 lg:grid-cols-2 lg:items-center lg:py-20">
          <div>
            <SectionLabel>Trust &amp; Safety</SectionLabel>
            <h1 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight text-ff-blue sm:text-5xl">
              Strict by design, honest by principle
            </h1>
            <p className="mt-5 max-w-xl text-ff-dark">
              FightFund reviews campaigns and payout requests before funds are released. We control funds
              through payment-partner rails and monitor campaigns continuously.
            </p>
            <div className="mt-6 rounded-xl border border-ff-border bg-[#F3F4F6] p-5 text-sm text-ff-dark">
              We never claim a campaign is <span className="font-semibold">“guaranteed safe”</span> or
              <span className="font-semibold"> “fraud-proof.”</span> No platform can. Instead, we tell you
              exactly what we've reviewed and where a campaign stands.
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-ff-border shadow-md">
            <img src={SAFETY_IMG} alt="Secure vault representing controlled payouts" className="h-full max-h-[80vh] w-full object-cover" />
          </div>
        </Container>
      </section>

      <section className="bg-[#F3F4F6]">
        <Container className="py-16">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((p) => (
              <div key={p.t} className="rounded-xl border border-ff-border bg-white p-8 shadow-sm">
                <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-ff-blue text-white">
                  <p.icon className="h-6 w-6" />
                </span>
                <h3 className="font-heading text-lg font-bold text-ff-blue">{p.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ff-dark">{p.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-white">
        <Container className="flex flex-col items-center gap-4 py-16 text-center">
          <h2 className="font-heading text-2xl font-bold text-ff-blue sm:text-3xl">See something concerning?</h2>
          <p className="max-w-lg text-ff-dark">Reporting takes less than a minute and helps protect donors and beneficiaries.</p>
          <Button asChild size="lg" variant="outline" className="border-ff-red font-semibold text-ff-red hover:bg-ff-red hover:text-white">
            <Link to="/report-campaign" data-testid="ts-report">Report a Campaign</Link>
          </Button>
        </Container>
      </section>
    </div>
  );
}
